import { getApiBaseUrl } from '../../config/runtimeConfig';
import { sessionFetch } from '../../services/session';


function httpError(message, status) {
  const error = new Error(message);
  error.status = status;
  return error;
}

async function request(path, options = {}) {
  let response;
  try {
    response = await sessionFetch(`${getApiBaseUrl()}${path}`, {
      credentials: 'include',
      ...options,
      headers: { Accept: 'application/json', ...options.headers },
      signal: AbortSignal.timeout(20000),
    });
  } catch (error) {
    throw httpError(error.name === 'TimeoutError'
      ? 'The request timed out. Please try again.'
      : 'Unable to connect. Please check your connection and try again.');
  }

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw httpError(
      typeof data?.message === 'string' ? data.message : 'Unable to complete the warehouse request.',
      response.status,
    );
  }
  return data;
}

export function isUnpersistedWarehouseError(error) {
  return !error?.status || error.status === 404;
}

function toWarehouse(row, fallback = {}) {
  return {
    id: row.id,
    name: row.name || '',
    code: row.code || '',
    address: row.address || '',
    city: row.city || '',
    country: row.country || '',
    country_id: row.country_id || null,
    capacity: row.capacity == null ? 0 : Number(row.capacity),
    capacityUnit: fallback.capacityUnit || 'm³',
    is_deleted: false,
  };
}

let countriesInFlight = null;

export function listCountries() {
  if (!countriesInFlight) {
    countriesInFlight = request('/countries')
      .then(data => data.countries || [])
      .finally(() => { countriesInFlight = null; });
  }
  return countriesInFlight;
}

let warehousesInFlight = null;

export function listWarehouses() {
  if (!warehousesInFlight) {
    warehousesInFlight = (async () => {
      try {
        const data = await request('/warehouses');
        return (data.warehouses || []).map(row => toWarehouse(row));
      } catch (error) {
        if (isUnpersistedWarehouseError(error)) return [];
        throw error;
      }
    })().finally(() => { warehousesInFlight = null; });
  }
  return warehousesInFlight;
}

const moduleWarehousesInFlight = new Map();
export function listModuleWarehouses(module) {
  const paths = { Customers: '/customers/warehouses', Dashboard: '/dashboard/warehouses' };
  if (!paths[module]) throw new Error('Unsupported warehouse lookup module.');
  if (!moduleWarehousesInFlight.has(module)) {
    moduleWarehousesInFlight.set(module, request(paths[module]).then(data => data.warehouses || [])
      .finally(() => moduleWarehousesInFlight.delete(module)));
  }
  return moduleWarehousesInFlight.get(module);
}

export async function getWarehouse(id) {
  try {
    const data = await request(`/warehouses/${id}`);
    return data.warehouse || null;
  } catch (error) {
    if (isUnpersistedWarehouseError(error)) return null;
    throw error;
  }
}

export async function createWarehouse(payload) {
  const data = await request('/warehouses', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: payload.name,
      code: payload.code,
      address: payload.address,
      city: payload.city,
      country_id: payload.country_id,
      capacity: payload.capacity,
    }),
  });
  const warehouse = data.warehouse;
  if (!warehouse?.id) throw httpError('Warehouse was not saved to the database.');
  return toWarehouse(warehouse, payload);
}

export async function updateWarehouse(id, payload) {
  const data = await request(`/warehouses/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: payload.name,
      code: payload.code,
      address: payload.address,
      city: payload.city,
      country_id: payload.country_id,
      capacity: payload.capacity,
    }),
  });
  const warehouse = data.warehouse;
  if (!warehouse?.id) throw httpError('Warehouse was not saved to the database.');
  return toWarehouse(warehouse, payload);
}

export async function deleteWarehouse(id) {
  await request(`/warehouses/${id}`, { method: 'DELETE' });
}
